<p-toast></p-toast>
@if (isLoading) {
<p-progressBar mode="indeterminate" class="m-2"></p-progressBar>
}
<app-page-shell>

    <app-page-hero
        eyebrow="CRM Contracts"
        [title]="createEditRevision.revisionID ? 'Edit Contract Revision' : 'Create Contract Revision'"
        [subtitle]="'Manage agreement dates, commercial terms, files, and notes for revision# ' + (createEditRevision.revisionNumber || '-')">
        <div actions>
            <p-button label="Clear" icon="fa-solid fa-xmark" severity="secondary" [outlined]="true" (onClick)="clearRevisionForm()"></p-button>
            <p-button [label]="createEditRevision.revisionID ? 'Save Changes' : 'Create Revision'"
                [icon]="createEditRevision.revisionID ? 'fa-solid fa-floppy-disk' : 'fa-solid fa-plus'"
                [loading]="revisionCreationInProgress" (onClick)="createUpdateRevision()"></p-button>
        </div>
    </app-page-hero>

    <form class="overFlow" [formGroup]="createEditRevisionForm" novalidate autocomplete="off" autocorrect="off"
        autocapitalize="off" spellcheck="off">
        <div class="p-fluid flex flex-column gap-3">

            <!-- Dates & Financials Fieldset -->
            <app-page-form-section icon="fa-solid fa-calendar-days" title="Dates & Financials" subtitle="Set effective dates, contract value, and penalty rates.">
                <div class="formgrid grid">
                    <div class="field col-12 md:col-6">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-calendar"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-datePicker id="effectiveStartDate"
                                    [(ngModel)]="createEditRevision.effectiveStartDate"
                                    formControlName="effectiveStartDateInput" [showIcon]="false" dateFormat="dd/mm/yy"
                                    styleClass="w-full"></p-datePicker>
                                <label for="effectiveStartDate">Effective Start Date</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-6">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-calendar-times"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-datePicker id="endDate" [(ngModel)]="createEditRevision.endDate"
                                    formControlName="endDateInput" [showIcon]="false" dateFormat="dd/mm/yy"
                                    styleClass="w-full"></p-datePicker>
                                <label for="endDate">End Date</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-6">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-dollar"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-inputNumber id="totalAmount" [(ngModel)]="createEditRevision.totalAmount"
                                    formControlName="totalAmountInput" mode="currency" currency="SAR"
                                    styleClass="w-full"></p-inputNumber>
                                <label for="totalAmount">Total Amount</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-6">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-percentage"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-inputNumber id="penaltyRate" [(ngModel)]="createEditRevision.penaltyRate"
                                    formControlName="penaltyRateInput" suffix="%" styleClass="w-full"></p-inputNumber>
                                <label for="penaltyRate">Penalty Rate</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                </div>
            </app-page-form-section>

            <!-- Agreement & Terms Fieldset -->
            <app-page-form-section icon="fa-solid fa-handshake" title="Agreement & Terms" subtitle="Capture agreement flags, renewal periods, insurance, tax, and payment terms.">
                <div class="formgrid grid">
                    <div class="field col-12 md:col-6">
                        <div
                            class="flex justify-content-between align-items-center border-1 surface-border p-3 border-round">
                            <div class="flex align-items-center">
                                <i class="fa-solid fa-handshake mr-2 text-xl text-color-secondary"></i>
                                <label for="firstPartyAgreedInput">Seller (First Party) Agreed?</label>
                            </div>
                            <p-toggleswitch id="firstPartyAgreedInput" formControlName="firstPartyAgreedInput"
                                [(ngModel)]="createEditRevision.firstPartyAgreed"></p-toggleswitch>
                        </div>
                    </div>
                    <div class="field col-12 md:col-6">
                        <div
                            class="flex justify-content-between align-items-center border-1 surface-border p-3 border-round">
                            <div class="flex align-items-center">
                                <i class="fa-solid fa-handshake mr-2 text-xl text-color-secondary"></i>
                                <label for="secondPartyAgreedInput">Buyer (Second Party) Agreed?</label>
                            </div>
                            <p-toggleswitch id="secondPartyAgreedInput" formControlName="secondPartyAgreedInput"
                                [(ngModel)]="createEditRevision.secondPartyAgreed"></p-toggleswitch>
                        </div>
                    </div>
                    <div class="field col-12 md:col-6">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-calendar-plus"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-inputNumber id="agreementPeriod" formControlName="agreementPeriodInput"
                                    [(ngModel)]="createEditRevision.agreementPeriod" suffix=" Days"
                                    styleClass="w-full"></p-inputNumber>
                                <label for="agreementPeriod">Agreement Period</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-6">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-calendar-minus"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-inputNumber id="nonRenewalDisclosure"
                                    formControlName="nonRenewalDisclosureBeforeDaysInput"
                                    [(ngModel)]="createEditRevision.nonRenewalDisclosureBeforeDays" suffix=" Days"
                                    styleClass="w-full"></p-inputNumber>
                                <label for="nonRenewalDisclosure">Non-Renewal Disclosure</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                </div>
            </app-page-form-section>

            <!-- Insurance & Financials Fieldset -->
            <app-page-form-section icon="fa-solid fa-shield-halved" title="Insurance & Financials" subtitle="Define insurance responsibility, VAT behavior, and bank account details.">
                <div class="formgrid grid">
                    <div class="field col-12 md:col-6">
                        <div
                            class="flex justify-content-between align-items-center border-1 surface-border p-3 border-round">
                            <div class="flex align-items-center">
                                <i class="fa-solid fa-shield-halved mr-2 text-xl text-color-secondary"></i>
                                <label for="goodsInsuranceByFirstPartyInput">Goods Insurance By Seller (First Party)?</label>
                            </div>
                            <p-toggleswitch id="goodsInsuranceByFirstPartyInput"
                                formControlName="goodsInsuranceByFirstPartyInput"
                                [(ngModel)]="createEditRevision.goodsInsuranceByFirstParty"></p-toggleswitch>
                        </div>
                    </div>
                    <div class="field col-12 md:col-6">
                        <div
                            class="flex justify-content-between align-items-center border-1 surface-border p-3 border-round">
                            <div class="flex align-items-center">
                                <i class="fa-solid fa-shield-halved mr-2 text-xl text-color-secondary"></i>
                                <label for="goodsInsuranceBySecondPartyInput">Goods Insurance By Buyer (Second Party)?</label>
                            </div>
                            <p-toggleswitch id="goodsInsuranceBySecondPartyInput"
                                formControlName="goodsInsuranceBySecondPartyInput"
                                [(ngModel)]="createEditRevision.goodsInsuranceBySecondParty"></p-toggleswitch>
                        </div>
                    </div>
                    <div class="field col-12 md:col-6">
                        <div
                            class="flex justify-content-between align-items-center border-1 surface-border p-3 border-round">
                            <div class="flex align-items-center">
                                <i class="fa-solid fa-file-invoice-dollar mr-2 text-xl text-color-secondary"></i>
                                <label for="vatAddedToInvoiceInput">VAT Added To Invoice?</label>
                            </div>
                            <p-toggleswitch id="vatAddedToInvoiceInput" formControlName="vatAddedToInvoiceInput"
                                [(ngModel)]="createEditRevision.vatAddedToInvoice"></p-toggleswitch>
                        </div>
                    </div>
                    <div class="field col-12 md:col-6">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-money-bill"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <input id="paymentTerms" pInputText formControlName="paymentTermsInput"
                                    [(ngModel)]="createEditRevision.paymentTerms" />
                                <label for="paymentTerms">Payment Terms</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-6">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-building-columns"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-select id="firstPartyBankAccountId"
                                    [options]="firstPartyBankAccounts"
                                    optionLabel="accountName"
                                    optionValue="id"
                                    [filter]="true"
                                    filterBy="accountName,accountNumber,iban"
                                    [showClear]="true"
                                    [loading]="bankAccountsLoading"
                                    placeholder="Select bank account"
                                    formControlName="firstPartyBankAccountIdInput"
                                    [(ngModel)]="createEditRevision.firstPartyBankAccountId"
                                    (onChange)="onFirstPartyBankAccountChange($event.value)"
                                    styleClass="w-full"></p-select>
                                <label for="firstPartyBankAccountId">Seller (First Party) Bank Account</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-6">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-credit-card"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <input id="firstPartyBankAccountDetails" pInputText
                                    formControlName="firstPartyBankAccountDetailsInput"
                                    [(ngModel)]="createEditRevision.firstPartyBankAccountDetails" />
                                <label for="firstPartyBankAccountDetails">Seller (First Party) Bank Account Details</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                </div>
            </app-page-form-section>

            <!-- Service & Legal Fieldset -->
            <app-page-form-section icon="fa-solid fa-scale-balanced" title="Service & Legal" subtitle="Record SLA, notice, force majeure, dispute, and governing law terms.">
                <div class="formgrid grid">
                    <div class="field col-12 md:col-6">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-clock"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-inputNumber id="siteServiceTime" formControlName="siteServiceTimeInput"
                                    [(ngModel)]="createEditRevision.siteServiceTime" suffix=" Hours"
                                    styleClass="w-full"></p-inputNumber>
                                <label for="siteServiceTime">Site Service Time</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-6">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-exclamation-triangle"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-inputNumber id="penaltyOnServiceTime" formControlName="penaltyOnServiceTimeInput"
                                    [(ngModel)]="createEditRevision.penaltyOnServiceTime" mode="currency" currency="SAR"
                                    styleClass="w-full"></p-inputNumber>
                                <label for="penaltyOnServiceTime">Penalty On Service Time</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-6">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-bell"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-inputNumber id="noticePeriod" formControlName="noticePeriodInput"
                                    [(ngModel)]="createEditRevision.noticePeriod" suffix=" Days"
                                    styleClass="w-full"></p-inputNumber>
                                <label for="noticePeriod">Notice Period</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-6">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-bolt"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <p-inputNumber id="forceMajeureClausePeriod"
                                    formControlName="forceMajeureClausePeriodInput"
                                    [(ngModel)]="createEditRevision.forceMajeureClausePeriod" suffix=" Days"
                                    styleClass="w-full"></p-inputNumber>
                                <label for="forceMajeureClausePeriod">Force Majeure Clause Period</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-6">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-gavel"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <input id="disputeResolution" pInputText formControlName="disputeResolutionInput"
                                    [(ngModel)]="createEditRevision.disputeResolution" />
                                <label for="disputeResolution">Dispute Resolution</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                    <div class="field col-12 md:col-6">
                        <p-inputGroup>
                            <p-inputGroupAddon><i class="pi pi-book"></i></p-inputGroupAddon>
                            <p-iftaLabel>
                                <input id="governingLaw" pInputText formControlName="governingLawInput"
                                    [(ngModel)]="createEditRevision.governingLaw" />
                                <label for="governingLaw">Governing Law</label>
                            </p-iftaLabel>
                        </p-inputGroup>
                    </div>
                </div>
            </app-page-form-section>

            <!-- Documents & Notes Fieldset -->
            <app-page-form-section icon="fa-solid fa-folder-open" title="Documents & Notes" subtitle="Upload original and signed agreements and capture internal revision notes.">
                <div class="formgrid grid">
                    <div class="field col-12 md:col-6">
                        <p-fileUpload name="originalAgreementFile" accept=".pdf,.doc,.docx" mode="basic"
                            [customUpload]="true" (onSelect)="onOriginalAgreementFileUpload($event)"
                            chooseLabel="Upload Original Agreement" chooseIcon="pi pi-file"
                            styleClass="w-full"></p-fileUpload>
                    </div>
                    <div class="field col-12 md:col-6">
                        <p-fileUpload name="signedAgreementFile" accept=".pdf,.doc,.docx" mode="basic"
                            [customUpload]="true" (onSelect)="onSignedAgreementFileUpload($event)"
                            chooseLabel="Upload Signed Agreement" chooseIcon="pi pi-file-edit"
                            styleClass="w-full"></p-fileUpload>
                    </div>
                    <div class="field col-12">
                        <p-iftaLabel>
                            <textarea id="revisionNotes" pInputTextarea formControlName="revisionNotesInput"
                                [(ngModel)]="createEditRevision.revisionNotes" [rows]="5" [cols]="30"
                                class="w-full"></textarea>
                            <label for="revisionNotes">Revision Notes</label>
                        </p-iftaLabel>
                    </div>
                </div>
            </app-page-form-section>
        </div>
    </form>
</app-page-shell>
